import { MAX_PRODUCT_MEDIA_SIZE_BYTES } from "@shared/products/photo-constraints";

export const WEBP_QUALITY = 0.82;
const JPEG_QUALITIES = [0.82, 0.7, 0.6] as const;
const EDGE_STEP = 0.75;
const MIN_EDGE_PX = 640;

type EncodeSize = { width: number; height: number };
type PaintContext = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

function paint(
  context: PaintContext,
  bitmap: ImageBitmap,
  size: EncodeSize,
  mimeType: string,
) {
  if (mimeType === "image/jpeg") {
    context.fillStyle = "white";
    context.fillRect(0, 0, size.width, size.height);
  }
  context.drawImage(bitmap, 0, 0, size.width, size.height);
}

async function drawAndEncode(
  bitmap: ImageBitmap,
  size: EncodeSize,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  if (typeof OffscreenCanvas === "function") {
    const canvas = new OffscreenCanvas(size.width, size.height);
    const context = canvas.getContext("2d");
    if (!context) return null;
    paint(context, bitmap, size, mimeType);
    return canvas.convertToBlob({ type: mimeType, quality });
  }
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  paint(context, bitmap, size, mimeType);
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
}

function fitsStorage(blob: Blob): boolean {
  return blob.size <= MAX_PRODUCT_MEDIA_SIZE_BYTES;
}

function shrink(size: EncodeSize): EncodeSize {
  return {
    width: Math.max(1, Math.round(size.width * EDGE_STEP)),
    height: Math.max(1, Math.round(size.height * EDGE_STEP)),
  };
}

async function encodeJpegWithinStorage(
  bitmap: ImageBitmap,
  size: EncodeSize,
): Promise<Blob | null> {
  let currentSize = size;
  for (;;) {
    let attempt: Blob | null = null;
    for (const quality of JPEG_QUALITIES) {
      attempt = await drawAndEncode(bitmap, currentSize, "image/jpeg", quality);
      if (!attempt || fitsStorage(attempt)) return attempt;
    }
    if (Math.max(currentSize.width, currentSize.height) <= MIN_EDGE_PX) {
      return attempt;
    }
    currentSize = shrink(currentSize);
  }
}

export async function encodePhotoForStorage(
  bitmap: ImageBitmap,
  width: number,
  height: number,
): Promise<Blob | null> {
  const size = { width, height };
  const webp = await drawAndEncode(bitmap, size, "image/webp", WEBP_QUALITY);
  if (webp?.type === "image/webp" && fitsStorage(webp)) return webp;
  return encodeJpegWithinStorage(bitmap, size);
}

export function extensionFor(mimeType: string): string {
  if (mimeType === "image/jpeg") return "jpg";
  if (mimeType === "image/png") return "png";
  return "webp";
}
